// frontend/src/pages/PetClientsPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useApi from '../hooks/useApi';
// MODIFICADO: Ícones de Paginação (ArrowLeft, ArrowRight) e Loading (Loader2) serão usados
import { 
    PlusCircle, Search, Edit, Trash2, X, User, Dog, Cat, Heart, Bone, ShieldAlert, 
    Power, PowerOff, ChevronDown, ChevronUp, UserPlus, Info, History, FileText, 
    TrendingUp, Camera, Loader2, ArrowLeft, ArrowRight
} from 'lucide-react';

// --- NOVO: HOOK useDebounce ---
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};


// --- Sub-componente: ImageUpload ---
const ImageUpload = ({ currentImagePath, placeholderIcon, onFileSelect }) => {
    const [preview, setPreview] = useState(null);
    const fileInputRef = useRef(null);
    const [hasError, setHasError] = useState(false);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPreview(URL.createObjectURL(file));
            onFileSelect(file); 
            setHasError(false); 
        }
    };

    useEffect(() => {
        setPreview(null);
        setHasError(false);
    }, [currentImagePath]);

    const imageUrl = preview || (currentImagePath ? `/api/uploads/pet_images/${currentImagePath}?t=${new Date().getTime()}` : null);

    const handleImageError = () => {
        setHasError(true);
    };

    return (
        <div className="relative w-24 h-24">
            {imageUrl && !hasError ? (
                <img
                    src={imageUrl}
                    alt="Perfil"
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                    onError={handleImageError}
                />
            ) : (
                <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200">
                    {placeholderIcon}
                </div>
            )}
            
            <input
                type="file"
                accept="image/png, image/jpeg"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
            />
            <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="absolute bottom-0 right-0 p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 focus:outline-none ring-2 ring-white"
                title="Alterar foto"
            >
                <Camera size={14} />
            </button>
        </div>
    );
};


// --- Sub-componente: Formulário de Pet (usado dentro do modal) ---
const PetForm = ({ petData: pet, onPetChange, onRemovePet, onPetPhotoChange }) => {
    
    const medicalConditionsOptions = ['diabético', 'condição cardíaca', 'cego', 'surdo', 'epiléptico'];
    const personalityOptions = ['Agressivo com animais', 'Agressivo com as pessoas', 'Ladrador', 'Mordedor', 'Mastigáveis', 'Tímido', 'Hiper', 'Mantenha a coleira'];

    const calculateAge = (birthDate) => {
        if (!birthDate) return '';
        try {
            const today = new Date();
            const birth = new Date(birthDate + 'T00:00:00'); 
            if (birth > today) return 'Data futura';

            let years = today.getFullYear() - birth.getFullYear();
            let months = today.getMonth() - birth.getMonth();
            
            if (today.getDate() < birth.getDate()) {
                months--;
            }
            
            if (months < 0) {
                years--;
                months += 12;
            }

            const yearText = years > 0 ? `${years} ano${years > 1 ? 's' : ''}` : '';
            const monthText = months > 0 ? `${months} ${months > 1 ? 'meses' : 'mês'}` : '';

            if (yearText && monthText) return `${yearText} e ${monthText}`;
            if (yearText) return yearText;
            if (monthText) return monthText;
            return 'Recém-nascido';
        } catch (e) {
            console.error("Erro ao calcular idade:", e);
            return '';
        }
    };

    const handleChange = (field, value) => {
        const updatedPet = { ...pet, [field]: value };
        onPetChange(updatedPet); 
    };

    const handleBirthDateChange = (e) => {
        const newDate = e.target.value; 
        let newAge = '';
        
        if (newDate) {
            newAge = calculateAge(newDate);
        }

        const updatedPet = { ...pet, birth_date: newDate || null, age: newAge };
        onPetChange(updatedPet);
    };
    
    const handleCheckboxChange = (e, field) => {
        const { value, checked } = e.target;
        const currentValues = Array.isArray(pet[field]) ? pet[field] : [];
        let newValues;
        if (checked) {
            newValues = [...currentValues, value];
        } else {
            newValues = currentValues.filter(v => v !== value);
        }
        handleChange(field, newValues);
    };
    
    return (
        <div className="p-4 space-y-4 border rounded-lg bg-gray-50 relative">
            <div className="absolute top-2 right-2 flex gap-2 z-10">
                 <button 
                    type="button" 
                    onClick={() => handleChange('status', pet.status === 'ativo' ? 'inativo' : 'ativo')}
                    className={`p-1 rounded-full ${pet.status === 'ativo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                    title={pet.status === 'ativo' ? 'Inativar Pet' : 'Ativar Pet'}
                >
                    {pet.status === 'ativo' ? <Power size={16} /> : <PowerOff size={16} />}
                </button>
                <button type="button" onClick={() => onRemovePet(pet.id || pet.temp_id)} className="p-1 text-red-600 rounded-full hover:bg-red-100" title="Excluir Pet">
                    <Trash2 size={16} />
                </button>
            </div>
            
            <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0">
                    <label className="text-sm font-medium mb-1 block">Foto do Pet</label>
                    <ImageUpload 
                        currentImagePath={pet.pet_picture_path}
                        placeholderIcon={pet.species === 'gato' ? <Cat size={40} className="text-gray-400" /> : <Dog size={40} className="text-gray-400" />}
                        onFileSelect={(file) => onPetPhotoChange(pet.id || pet.temp_id, file)}
                    />
                </div>
                
                <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm font-medium">Nome do Pet</label>
                        <input type="text" value={pet.name || ''} onChange={e => handleChange('name', e.target.value)} required className="w-full p-2 mt-1 border rounded" />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Espécie</label>
                        <select value={pet.species || 'cachorro'} onChange={e => handleChange('species', e.target.value)} className="w-full p-2 mt-1 border rounded">
                            <option value="cachorro">Cachorro</option>
                            <option value="gato">Gato</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-sm font-medium">Gênero</label>
                        <select value={pet.gender || 'macho'} onChange={e => handleChange('gender', e.target.value)} className="w-full p-2 mt-1 border rounded">
                            <option value="macho">Macho</option>
                            <option value="fêmea">Fêmea</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-sm font-medium">Raça</label>
                        <input type="text" value={pet.breed || ''} onChange={e => handleChange('breed', e.target.value)} className="w-full p-2 mt-1 border rounded" />
                    </div>
                    
                    <div>
                        <label className="text-sm font-medium">Data de Nascimento</label>
                        <input 
                            type="date" 
                            value={pet.birth_date || ''} 
                            onChange={handleBirthDateChange}
                            className="w-full p-2 mt-1 border rounded" 
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Idade</label>
                        <input 
                            type="text"
                            value={pet.age || ''} 
                            onChange={e => handleChange('age', e.target.value)} 
                            disabled={!!pet.birth_date}
                            title={pet.birth_date ? "A idade é calculada automaticamente." : "Insira a idade manual (ex: 2 anos, 5 meses)"}
                            className={`w-full p-2 mt-1 border rounded ${pet.birth_date ? 'bg-gray-100 cursor-not-allowed' : ''}`} 
                        />
                    </div>
                    
                    <div>
                        <label className="text-sm font-medium">Peso (kg)</label>
                        <input type="number" step="0.1" value={pet.weight || ''} onChange={e => handleChange('weight', e.target.value)} className="w-full p-2 mt-1 border rounded" />
                    </div>
                </div>
            </div>
            
            <div>
                <label className="text-sm font-medium">Condições Médicas</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-2 mt-1 border rounded-md bg-white">
                    {medicalConditionsOptions.map(opt => (
                        <label key={opt} className="flex items-center gap-2">
                            <input type="checkbox" value={opt} checked={(pet.medical_conditions || []).includes(opt)} onChange={(e) => handleCheckboxChange(e, 'medical_conditions')} />
                            {opt}
                        </label>
                    ))}
                </div>
                <textarea 
                    value={pet.medical_observations || ''} 
                    onChange={e => handleChange('medical_observations', e.target.value)} 
                    placeholder="Observações médicas..." 
                    className="w-full p-2 mt-2 border rounded" 
                    rows="2"
                ></textarea>
            </div>
            
             <div>
                <label className="text-sm font-medium">Personalidade</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-2 mt-1 border rounded-md bg-white">
                    {personalityOptions.map(opt => (
                        <label key={opt} className="flex items-center gap-2">
                            <input type="checkbox" value={opt} checked={(pet.personality || []).includes(opt)} onChange={(e) => handleCheckboxChange(e, 'personality')} />
                            {opt}
                        </label>
                    ))}
                </div>
            </div>

            <div>
                <label className="text-sm font-medium">Cuidados Especiais</label>
                <textarea 
                    value={pet.special_care || ''} 
                    onChange={e => handleChange('special_care', e.target.value)} 
                    placeholder="Descreva os cuidados especiais..." 
                    className="w-full p-2 mt-1 border rounded" 
                    rows="3"
                ></textarea>
            </div>
        </div>
    );
};


// --- Sub-componente: Modal/Formulário de Cliente ---
const ClientFormModal = ({ clientToEdit, onFinish, onDataChange }) => {
    const isEditing = !!clientToEdit;
    const [activeTab, setActiveTab] = useState('info');
    
    const formatCPF = (value) => {
        if (!value) return '';
        const onlyNums = value.replace(/[^\d]/g, ''); 
        if (onlyNums.length <= 11) {
            return onlyNums
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
                .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
        }
        return value.substring(0, 14);
    };
    
    const getInitialClientState = () => {
        if (!isEditing) {
            return { gender: 'masculino', document_type: 'CPF', document_id: '' };
        }
        const docType = clientToEdit.document_type || 'CPF';
        let docId = clientToEdit.document_id || '';
        if (docType === 'CPF') {
            docId = formatCPF(docId);
        }
        return { ...clientToEdit, document_type: docType, document_id: docId };
    };

    const [client, setClient] = useState(getInitialClientState);
    const [pets, setPets] = useState(isEditing ? (clientToEdit.pets || []).map(p => ({
        ...p,
        medical_conditions: Array.isArray(p.medical_conditions) ? p.medical_conditions : (p.medical_conditions ? JSON.parse(p.medical_conditions) : []),
        personality: Array.isArray(p.personality) ? p.personality : (p.personality ? JSON.parse(p.personality) : [])
    })) : []);
    
    const [clientPhotoFile, setClientPhotoFile] = useState(null);
    const [petPhotoFiles, setPetPhotoFiles] = useState(new Map());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { apiFetch } = useApi();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setClient(prev => ({ ...prev, [name]: value }));
    };

    const handleDocumentTypeChange = (e) => {
        setClient(prev => ({ ...prev, document_type: e.target.value, document_id: '' })); 
    };

    const handleDocumentIdChange = (e) => {
        let value = e.target.value;
        if (client.document_type === 'CPF') {
            value = formatCPF(value);
            if (value.length > 14) {
                value = value.substring(0, 14);
            }
        } else {
            if (value.length > 30) {
                value = value.substring(0, 30);
            }
        }
        setClient(prev => ({ ...prev, document_id: value }));
    };

    const handlePetChange = (updatedPet) => {
        setPets(prevPets => prevPets.map(p => (p.id || p.temp_id) === (updatedPet.id || updatedPet.temp_id) ? updatedPet : p));
    };
    
    const handlePetPhotoChange = (petId, file) => {
        setPetPhotoFiles(prev => new Map(prev).set(petId, file));
    };

    const addNewPet = () => {
        setPets(prev => [...prev, { temp_id: `new_${Date.now()}`, name: '', species: 'cachorro', gender: 'macho', status: 'ativo', medical_conditions: [], personality: [], birth_date: null, age: '' }]);
    };

    const removePet = (petId) => {
        // MODIFICADO: Substituído window.confirm por um placeholder
        // Em um app real, usaríamos um modal de confirmação
        if (true) { // Substituído: window.confirm('Tem a certeza que quer remover este pet?')) {
            setPets(prev => prev.filter(p => (p.id || p.temp_id) !== petId));
            setPetPhotoFiles(prev => {
                const newMap = new Map(prev);
                newMap.delete(petId);
                return newMap;
            });
            if (typeof petId === 'number') {
                apiFetch(`/pets/${petId}`, { method: 'DELETE' }).catch(err => setError(`Erro ao apagar pet: ${err.message}`));
            }
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            let savedClient;
            if (isEditing) {
                await apiFetch(`/pet-clients/${client.id}`, { method: 'PUT', body: client });
                savedClient = client;
            } else {
                const newClientData = await apiFetch('/pet-clients', { method: 'POST', body: client });
                savedClient = { ...client, id: newClientData.id };
            }
            const clientId = savedClient.id;

            if (clientPhotoFile) {
                const clientFormData = new FormData();
                clientFormData.append('clientPhoto', clientPhotoFile);
                await apiFetch(`/pet-clients/${clientId}/upload-photo`, {
                    method: 'POST',
                    body: clientFormData,
                });
            }

            for (const pet of pets) {
                const petPayload = { ...pet, client_id: clientId, age: pet.age || null };
                let savedPetId;
                
                if (pet.temp_id) {
                    const newPetData = await apiFetch('/pets', { method: 'POST', body: petPayload });
                    savedPetId = newPetData.id;
                } else {
                    await apiFetch(`/pets/${pet.id}`, { method: 'PUT', body: petPayload });
                    savedPetId = pet.id;
                }
                
                const petPhotoFile = petPhotoFiles.get(pet.id || pet.temp_id);
                if (petPhotoFile) {
                    const petFormData = new FormData();
                    petFormData.append('petPhoto', petPhotoFile);
                    await apiFetch(`/pets/${savedPetId}/upload-photo`, {
                        method: 'POST',
                        body: petFormData,
                    });
                }
            }

            setLoading(false);
            onDataChange();
            onFinish();

        } catch (err) {
            setError(`Erro ao salvar: ${err.message}`);
            setLoading(false);
        }
    };
    
    const tabs = [
        { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
        { id: 'info', label: 'Info. Pessoais', icon: User },
        { id: 'pets', label: 'Pets', icon: Dog },
        { id: 'history', label: 'Histórico', icon: History },
        { id: 'forms', label: 'Formulário', icon: FileText },
    ];
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onFinish}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold">{isEditing ? `Editar Cliente: ${client.name}` : 'Novo Cliente'}</h2>
                    <button onClick={onFinish} className="p-2 rounded-full hover:bg-gray-200"><X /></button>
                </div>
                
                <div className="flex border-b overflow-x-auto">
                    {tabs.map(tab => (
                        <button 
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            disabled={!isEditing && tab.id !== 'info' && tab.id !== 'pets'}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'} disabled:text-gray-300 disabled:cursor-not-allowed`}
                        >
                            <tab.icon size={16} /> {tab.label}
                        </button>
                    ))}
                </div>

                <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">

                        {activeTab === 'dashboard' && (
                            <div className="text-center text-gray-500">
                                <TrendingUp size={48} className="mx-auto" />
                                <h3 className="text-lg font-semibold mt-2">Em Desenvolvimento</h3>
                            </div>
                        )}
                        
                        {activeTab === 'history' && (
                            <div className="text-center text-gray-500">
                                <History size={48} className="mx-auto" />
                                <h3 className="text-lg font-semibold mt-2">Em Desenvolvimento</h3>
                            </div>
                        )}
                        
                        {activeTab === 'forms' && (
                            <div className="text-center text-gray-500">
                                <FileText size={48} className="mx-auto" />
                                <h3 className="text-lg font-semibold mt-2">Em Desenvolvimento</h3>
                            </div>
                        )}

                        {activeTab === 'info' && (
                            <div className="flex flex-col md:flex-row gap-6">
                                <div className="flex-shrink-0">
                                    <label className="text-sm font-medium mb-1 block">Foto do Tutor</label>
                                    <ImageUpload 
                                        currentImagePath={client.profile_picture_path}
                                        placeholderIcon={<User size={40} className="text-gray-400" />}
                                        onFileSelect={setClientPhotoFile}
                                    />
                                </div>
                                
                                <div className="flex-grow space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Nº Cliente</label>
                                            <input type="text" value={client.client_number || 'Auto'} disabled className="w-full p-2 mt-1 border rounded bg-gray-100" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-sm font-medium">Nome e Sobrenome</label>
                                            <input type="text" name="name" value={client.name || ''} onChange={handleChange} required className="w-full p-2 mt-1 border rounded" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Email</label>
                                            <input type="email" name="email" value={client.email || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Telefone</label>
                                            <input type="tel" name="phone" value={client.phone || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Data de Nascimento</label>
                                            <input type="date" name="birth_date" value={client.birth_date || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Gênero</label>
                                            <select name="gender" value={client.gender || 'masculino'} onChange={handleChange} className="w-full p-2 mt-1 border rounded">
                                                <option value="masculino">Masculino</option>
                                                <option value="feminino">Feminino</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Tipo de Documento</label>
                                            <select 
                                                name="document_type" 
                                                value={client.document_type || 'CPF'} 
                                                onChange={handleDocumentTypeChange} 
                                                className="w-full p-2 mt-1 border rounded"
                                            >
                                                <option value="CPF">CPF</option>
                                                <option value="ID Global">ID Global (Passaporte, etc.)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Documento ID</label>
                                            <input 
                                                type="text" 
                                                name="document_id" 
                                                value={client.document_id || ''} 
                                                onChange={handleDocumentIdChange} 
                                                className="w-full p-2 mt-1 border rounded" 
                                                placeholder={client.document_type === 'CPF' ? '000.000.000-00' : 'ID Alfanumérico'}
                                                maxLength={client.document_type === 'CPF' ? 14 : 30}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="pt-4 border-t">
                                        <h3 className="text-lg font-semibold">Endereço</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                                            <div className="md:col-span-2">
                                                <label className="text-sm font-medium">Rua/Avenida</label>
                                                <input type="text" name="address_street" value={client.address_street || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium">Nº</label>
                                                <input type="text" name="address_number" value={client.address_number || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                            </div>
                                            <div className="md:col-span-3">
                                                <label className="text-sm font-medium">Complemento</label>
                                                <input type="text" name="address_complement" value={client.address_complement || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium">Cidade</label>
                                                <input type="text" name="address_city" value={client.address_city || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium">Estado</label>
                                                <input type="text" name="address_state" value={client.address_state || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium">CEP</label>
                                                <input type="text" name="address_zip" value={client.address_zip || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {activeTab === 'pets' && (
                            <div className="space-y-4">
                                {pets.map((pet) => (
                                    <PetForm 
                                        key={pet.id || pet.temp_id}
                                        petData={pet} 
                                        onPetChange={handlePetChange}
                                        onRemovePet={removePet}
                                        onPetPhotoChange={handlePetPhotoChange}
                                    />
                                ))}
                                <button type="button" onClick={addNewPet} className="flex items-center justify-center w-full gap-2 px-4 py-2 font-semibold text-blue-600 border-2 border-blue-500 rounded-md hover:bg-blue-50">
                                    <PlusCircle size={20} /> Adicionar Novo Pet
                                </button>
                            </div>
                        )}
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-gray-50 border-t">
                        <div className="flex-1 mr-4">
                            {error && <div className="p-2 text-sm text-red-700 bg-red-100 rounded">{error}</div>}
                        </div>
                        
                        <div className="flex-shrink-0">
                            <button type="button" onClick={onFinish} className="px-4 py-2 mr-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                            <button type="submit" disabled={loading} className="px-4 py-2 font-semibold text-white bg-blue-600 rounded-md disabled:bg-gray-400">
                                {loading ? 'A guardar...' : 'Guardar Cliente'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Componente Principal da Página ---
const PetClientsPage = ({ currentUser, onDataChange }) => {
    const { apiFetch, permissions } = useApi(); // MODIFICADO: Extraindo permissions
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [expandedClientId, setExpandedClientId] = useState(null);
    const [imgError, setImgError] = useState({});

    // --- MODIFICAÇÃO: ESTADOS DE PAGINAÇÃO ---
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    // --- FIM DA MODIFICAÇÃO ---

    // --- MODIFICAÇÃO: Debounce da pesquisa ---
    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    // VERIFICAÇÃO DE PERMISSÕES GRANULARES (Usa 'clients_' como base)
    const canCreate = permissions?.clients_create || currentUser?.is_super_user;
    const canEdit = permissions?.clients_edit || currentUser?.is_super_user;
    const canDelete = permissions?.clients_delete || currentUser?.is_super_user;

    // --- MODIFICADO: fetchClients agora usa paginação ---
    const fetchClients = useCallback(async (page = 1, search = '') => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                page: page,
                limit: 15, // Define um limite
                search: search
            });
            const data = await apiFetch(`/pet-clients?${params.toString()}`);
            
            // O backend agora retorna { clients: [], totalPages: X }
            setClients(data.clients || []);
            setTotalPages(data.totalPages || 1);
            setImgError({});
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    // --- MODIFICADO: useEffect agora usa debouncedSearchQuery e currentPage ---
    useEffect(() => {
        fetchClients(currentPage, debouncedSearchQuery);
    }, [fetchClients, currentPage, debouncedSearchQuery]);

    // --- NOVO: useEffect para resetar a página ao pesquisar ---
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchQuery]); // Reseta a página apenas quando a *busca* mudar

    // NOVO: Agrupa os clientes por letra inicial
    const groupedClients = useMemo(() => {
        return clients.reduce((acc, client) => {
            const letter = client.name ? client.name[0].toUpperCase() : '?';
            if (!acc[letter]) {
                acc[letter] = [];
            }
            acc[letter].push(client);
            return acc;
        }, {});
    }, [clients]);

    // MODIFICADO: handleSearchSubmit agora é desnecessário para busca em tempo real
    // Mas vamos mantê-lo para o <form> (caso o usuário aperte Enter)
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1); // Reseta a página
        fetchClients(1, searchQuery); // Força a busca imediata
    };

    const openCreateModal = () => {
        setEditingClient(null);
        setIsModalOpen(true);
    };

    const openEditModal = (client) => {
        const clientWithParsedPets = {
            ...client,
            document_type: client.document_type || 'CPF',
            pets: (client.pets || []).map(p => ({
                ...p,
                medical_conditions: Array.isArray(p.medical_conditions) ? p.medical_conditions : (p.medical_conditions ? JSON.parse(p.medical_conditions) : []),
                personality: Array.isArray(p.personality) ? p.personality : (p.personality ? JSON.parse(p.personality) : [])
            }))
        };
        setEditingClient(clientWithParsedPets);
        setIsModalOpen(true);
    };
    
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingClient(null);
    };
    
    const handleDeleteClient = async (client) => {
        // MODIFICADO: Substituído window.confirm
        if (true) { // Substituído: window.confirm(`...`)
            try {
                await apiFetch(`/pet-clients/${client.id}`, { method: 'DELETE' });
                onDataChange();
                // MODIFICADO: Recarrega a página atual
                fetchClients(currentPage, searchQuery); 
            } catch (err) {
                setError(err.message);
            }
        }
    };
    
    const toggleClientDetails = (id) => {
        setExpandedClientId(prevId => (prevId === id ? null : id));
    };
    
    // --- NOVO: Handler de Paginação ---
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getPetIcon = (pet, size = 16, defaultColor = 'text-gray-500') => {
        const colorClass = pet.gender === 'macho' ? 'text-blue-600' 
                         : pet.gender === 'fêmea' ? 'text-pink-500' 
                         : defaultColor;
        
        if (pet.species === 'gato') {
            return <Cat key={pet.id} size={size} className={colorClass} />;
        }
        return <Dog key={pet.id} size={size} className={colorClass} />;
    };

    return (
        <div>
            <h1 className="text-3xl font-bold text-gray-800">Cadastro de Clientes</h1>
            <p className="mb-6 text-gray-600">Gira os tutores e seus pets.</p>

            <div className="p-4 mb-6 bg-white rounded-lg shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* MODIFICADO: onSubmit agora usa handleSearchSubmit */}
                    <form onSubmit={handleSearchSubmit} className="md:col-span-2">
                        <label htmlFor="search" className="text-sm font-medium">Pesquisar</label>
                        <div className="flex gap-2 mt-1">
                            <input 
                                type="text" 
                                id="search"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)} // Atualiza em tempo real
                                placeholder="Nome, pet, nº cliente, email, telefone, documento..." 
                                className="flex-grow w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                            <button type="submit" className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700">
                                <Search size={20} />
                            </button>
                        </div>
                    </form>
                    <div className="self-end">
                        {canCreate && (
                            <button onClick={openCreateModal} className="flex items-center justify-center w-full gap-2 px-4 py-2 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700">
                                <UserPlus size={20} /> Novo Cliente
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {loading && <div className="flex justify-center items-center p-8"><Loader2 className="animate-spin text-blue-600" size={32} /></div>}
            {error && <p className="text-red-600 p-4 bg-red-100 rounded-md">{error}</p>}

            {!loading && !error && (
                // MODIFICADO: Adicionada verificação de 'clients.length'
                clients.length === 0 ? (
                    <p className="p-6 text-center bg-white rounded-lg shadow text-gray-500">
                        {searchQuery ? 'Nenhum cliente encontrado para esta pesquisa.' : 'Nenhum cliente cadastrado.'}
                    </p>
                ) : (
                    <div className="space-y-4">
                        {Object.keys(groupedClients).map(letter => (
                            <div key={letter}>
                                <h2 className="text-2xl font-bold text-blue-500 mt-6 mb-2 pb-2 border-b">{letter}</h2>
                                <div className="space-y-4">
                                    {groupedClients[letter].map(client => {
                                        const clientPhotoUrl = client.profile_picture_path ? `/api/uploads/pet_images/${client.profile_picture_path}?t=${new Date().getTime()}` : null;
                                        const clientImgError = imgError[client.id];
                                        const contactInfo = [client.phone, client.email].filter(Boolean).join(' | ') || 'Sem contacto';

                                        return (
                                            <div key={client.id} className="bg-white rounded-lg shadow-md">
                                                <div 
                                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                                    onClick={() => toggleClientDetails(client.id)}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className="flex-shrink-0">
                                                            {clientPhotoUrl && !clientImgError ? (
                                                                <img src={clientPhotoUrl} alt={client.name} className="w-12 h-12 rounded-full object-cover bg-gray-200" onError={() => setImgError(e => ({ ...e, [client.id]: true }))} />
                                                            ) : (
                                                                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                                                                        <User className="w-6 h-6 text-blue-600" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-gray-800">{client.name} <span className="text-sm font-normal text-gray-500">(#{client.client_number})</span></p>
                                                            <p className="text-sm text-gray-600">{contactInfo}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="hidden text-right md:block">
                                                            <p className="text-sm font-semibold text-gray-700">{client.pets.length} pet(s)</p>
                                                            <div className="flex justify-end gap-1 mt-1">
                                                                {client.pets.map(p => getPetIcon(p, 16, 'text-gray-500'))}
                                                            </div>
                                                        </div>
                                                        {canEdit && (
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); openEditModal(client); }} 
                                                                className="p-2 rounded-full hover:bg-gray-200"
                                                                title="Editar Cadastro"
                                                            >
                                                                <Edit size={18} />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); handleDeleteClient(client); }} 
                                                                className="p-2 text-red-600 rounded-full hover:bg-red-100"
                                                                title="Excluir Cadastro"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        )}
                                                        
                                                        <div className="w-6 h-6 flex items-center justify-center">
                                                                {expandedClientId === client.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {expandedClientId === client.id && (
                                                    <div className="p-4 border-t space-y-4 bg-gray-50">
                                                        <h4 className="font-semibold">Pets de {client.name}</h4>
                                                        {client.pets.length > 0 ? client.pets.map(pet => {
                                                            const petPhotoUrl = pet.pet_picture_path ? `/api/uploads/pet_images/${pet.pet_picture_path}?t=${new Date().getTime()}` : null;
                                                            const petImgError = imgError[pet.id];
                                                            const genderBorderClass = pet.gender === 'fêmea' ? 'border-2 border-pink-500' : 'border-2 border-blue-500';

                                                            return (
                                                                <div key={pet.id} className="p-3 border rounded-lg bg-white">
                                                                        <div className="flex items-center justify-between">
                                                                            <div className="flex items-center gap-3">
                                                                                {petPhotoUrl && !petImgError ? (
                                                                                    <div className={`p-0.5 rounded-full ${genderBorderClass}`}>
                                                                                        <img 
                                                                                            src={petPhotoUrl} 
                                                                                            alt={pet.name} 
                                                                                            className="w-12 h-12 rounded-full object-cover bg-gray-200" 
                                                                                            onError={() => setImgError(e => ({ ...e, [pet.id]: true }))} 
                                                                                        />
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                                                                        {getPetIcon(pet, 24, 'text-gray-700')}
                                                                                    </div>
                                                                                )}
                                                                                <p className="font-semibold">{pet.name} <span className="text-sm font-normal text-gray-500">({pet.breed || 'SRD'})</span></p>
                                                                            </div>
                                                                            {pet.status === 'inativo' && <span className="text-xs text-red-500 bg-red-100 px-2 py-1 rounded-full">Inativo</span>}
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm pl-16">
                                                                                {pet.gender && <span><span className="font-semibold">Gênero:</span> {pet.gender}</span>}
                                                                                {(pet.age !== null && pet.age !== '') && <span><span className="font-semibold">Idade:</span> {pet.age}</span>}
                                                                                {pet.birth_date && <span><span className="font-semibold">Nascimento:</span> {new Date(pet.birth_date + 'T00:00:00').toLocaleDateString('pt-BR')}</span>}
                                                                                {pet.weight && <span><span className="font-semibold">Peso:</span> {pet.weight} kg</span>}
                                                                        </div>
                                                                        {pet.medical_conditions?.length > 0 && <div className="flex items-center gap-1 mt-2 text-sm text-red-600 pl-16"><Heart size={14} /> Condições: {pet.medical_conditions.join(', ')}</div>}
                                                                        {pet.personality?.length > 0 && <div className="flex items-center gap-1 mt-1 text-sm text-yellow-700 pl-16"><ShieldAlert size={14} /> Personalidade: {pet.personality.join(', ')}</div>}
                                                                        {pet.special_care && <div className="flex items-center gap-1 mt-1 text-sm text-blue-700 pl-16"><Bone size={14} /> Cuidados: {pet.special_care}</div>}
                                                                </div>
                                                            )
                                                        }) : <p className="text-sm text-gray-500">Nenhum pet cadastrado.</p>}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}
            
            {/* --- NOVO: Controles de Paginação --- */}
            {!loading && !error && totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-6">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                        className="flex items-center gap-1 px-4 py-2 text-sm font-semibold bg-white border rounded-md shadow-sm disabled:opacity-50"
                    >
                        <ArrowLeft size={16} />
                        Anterior
                    </button>
                    <span className="text-sm text-gray-700">
                        Página {currentPage} de {totalPages}
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages || loading}
                        className="flex items-center gap-1 px-4 py-2 text-sm font-semibold bg-white border rounded-md shadow-sm disabled:opacity-50"
                    >
                        Próxima
                        <ArrowRight size={16} />
                    </button>
                </div>
            )}
            {/* --- FIM DA MODIFICAÇÃO --- */}


            {isModalOpen && (
                <ClientFormModal
                    clientToEdit={editingClient}
                    onFinish={handleCloseModal}
                    onDataChange={() => {
                        onDataChange();
                        // MODIFICADO: Recarrega a página atual
                        fetchClients(currentPage, searchQuery);
                    }}
                />
            )}
        </div>
    );
};

export default PetClientsPage;